import React, { useState } from 'react';
import {
    ArrowUpRight,
    ArrowDownLeft,
    Search,
    Calendar,
    CheckCircle2,
    Clock,
    XCircle,
    MoreVertical,
    RefreshCw,
    ArrowLeft,
    ArrowRightLeft,
    TrendingUp,
    Zap,
    History
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useTransactionManager } from '@/shared/hooks/useTransactionManager';
import { useAuth } from '@/shared/hooks/useAuth';


const HistoryPage: React.FC = () => {
    const { user } = useAuth();
    const { transactions, loading } = useTransactionManager(user?.uid);
    const [filterType, setFilterType] = useState('All');

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">
            <RefreshCw className="h-12 w-12 text-blue-500 animate-spin" />
        </div>;
    }

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'COMPLETED':
            case 'APPROVED':
            case 'REALIZED': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20';
            case 'ACTIVE':
            case 'PROCESSING': return 'bg-blue-500/10 text-blue-600 dark:text-blue-500 border-blue-500/20';
            case 'PENDING': return 'bg-amber-500/10 text-amber-600 dark:text-amber-500 border-amber-500/20';
            case 'REJECTED':
            case 'FAILED':
            case 'DISQUALIFIED': return 'bg-red-500/10 text-red-600 dark:text-red-500 border-red-500/20';
            default: return 'bg-muted text-muted-foreground border-border';
        }
    };

    const getIcon = (type: string) => {
        if (type.includes('TRADING')) return <Zap className="h-5 w-5" />;
        if (type === 'SWAP') return <ArrowRightLeft className="h-5 w-5" />;
        if (type === 'DEPOSIT') return <ArrowDownLeft className="h-5 w-5" />;
        if (type === 'STAKING') return <TrendingUp className="h-5 w-5" />;
        return <ArrowUpRight className="h-5 w-5" />;
    };

    const filteredTransactions = transactions.filter(tx => {
        if (!tx || !tx.type) return false;

        const typeStr = (tx.type as string || '').toUpperCase();
        const assetStr = (tx.asset as string || '').toUpperCase();

        // 입출금 내역은 모든 뷰에서 제외 (사용자 요청)
        if (typeStr === 'DEPOSIT' || typeStr === 'WITHDRAWAL' || typeStr === 'WITHDRAW') return false;

        if (filterType === 'All') return true;

        // P2P 거래 필터 (에셋 명에 'P2P' 포함)
        if (filterType === 'P2P' && assetStr.includes('P2P')) return true;

        // 스왑 필터 (P2P가 아닌 SWAP 타입)
        if (filterType === 'SWAP' && typeStr === 'SWAP' && !assetStr.includes('P2P')) return true;

        // 데이트레이딩 필터
        if (filterType === 'DAY_TRADING' && (typeStr === 'TRADING_BUY' || typeStr === 'TRADING_SELL')) return true;

        // 블록딜 필터
        if (filterType === 'BLOCK_DEAL' && (typeStr === 'BLOCK_DEAL' || assetStr.includes('BLOCK DEAL'))) return true;

        // ETF 필터
        if (filterType === 'ETF' && (typeStr.includes('ETF') || assetStr.includes('ETF'))) return true;

        // 스테이킹 필터
        if (filterType === 'STAKING' && (typeStr === 'STAKING' || typeStr === 'STAKE' || assetStr.includes('STAKING'))) return true;

        return false;
    });

    return (
        <div className="space-y-12 animate-in fade-in duration-1000 text-foreground pb-24">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8 px-4 lg:px-8">
                <div className="space-y-4 max-w-2xl">
                    <div className="flex items-center gap-2 text-muted-foreground cursor-pointer hover:text-foreground transition-colors group/back" onClick={() => window.history.back()}>
                        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-[10px] font-black uppercase tracking-widest">대시보드로 돌아가기</span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500 transition-colors">
                        <History className="h-3.5 w-3.5 fill-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">통합 거래 원장 시스템</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                        전체 거래 내역,<br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500">
                            통합 데이터 센터
                        </span>
                    </h1>
                    <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                        사용자의 모든 자금 흐름을 투명하게 확인하고 관리하세요.<br />
                        블록체인 네트워크와 실시간 동기화되는 신뢰할 수 있는 기록입니다.
                    </p>
                </div>
            </div>

            {/* Main Tabs Section */}
            <div className="px-4 lg:px-8">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2.5 p-2 bg-card border border-border rounded-[2.5rem] shadow-2xl transition-colors">
                    {[
                        { id: 'All', label: '전체 (ALL)' },
                        { id: 'SWAP', label: '스왑 (SWAP)' },
                        { id: 'DAY_TRADING', label: '데이트레이딩' },
                        { id: 'BLOCK_DEAL', label: '블록딜' },
                        { id: 'ETF', label: '가상자산 ETF' },
                        { id: 'STAKING', label: '스테이킹' },
                        { id: 'P2P', label: 'P2P 거래' }
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setFilterType(t.id)}
                            className={cn(
                                "h-14 rounded-[1.5rem] text-[9px] font-black uppercase tracking-widest transition-all",
                                filterType === t.id ? "bg-blue-600 text-white shadow-xl shadow-blue-600/20" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                            )}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* List Table Section */}
            <div className="px-4 lg:px-8">
                <div className="rounded-[3rem] bg-card border border-border overflow-hidden shadow-2xl transition-colors">
                    <div className="p-10 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-6 bg-accent/30">
                        <div className="space-y-1">
                            <h3 className="font-black flex items-center gap-3 text-foreground text-xl tracking-tighter uppercase">
                                <History className="h-6 w-6 text-blue-600 dark:text-blue-500" /> 거래 기록
                            </h3>
                            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.3em]">기관급 데이터 트랜잭션 히스토리</p>
                        </div>
                        <div className="relative group max-w-md w-full">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50 group-focus-within:text-blue-500 transition-colors" />
                            <input type="text" placeholder="검색어 입력..." className="h-14 w-full bg-accent border border-border rounded-2xl pl-12 pr-6 text-[10px] font-black outline-none focus:border-blue-500/50 text-foreground transition-all uppercase tracking-widest" />
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-accent/50 text-[10px] uppercase font-black tracking-[0.2em] text-muted-foreground">
                                <tr>
                                    <th className="px-10 py-6">자산 / 체결시간</th>
                                    <th className="px-10 py-6">구분</th>
                                    <th className="px-10 py-6 text-right">금액 / 수량</th>
                                    <th className="px-10 py-6 text-center">집행 상태</th>
                                    <th className="px-10 py-6 text-right">상세 내역</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredTransactions.map((tx) => (
                                    <tr key={tx.id} className="group hover:bg-accent/50 transition-all duration-500">
                                        <td className="px-10 py-8">
                                            <div className="flex items-center gap-6">
                                                <div className={cn(
                                                    "w-14 h-14 rounded-2xl flex items-center justify-center border border-border shadow-lg transition-transform group-hover:scale-110 duration-500",
                                                    tx.type === 'TRADING_BUY' ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-500' :
                                                        tx.type === 'TRADING_SELL' ? 'bg-rose-500/10 text-rose-600 dark:text-rose-500' :
                                                            tx.type === 'DEPOSIT' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500' : 'bg-blue-500/10 text-blue-600 dark:text-blue-500'
                                                )}>
                                                    {getIcon(tx.type)}
                                                </div>
                                                <div>
                                                    <p className="text-base font-black text-foreground leading-none uppercase group-hover:text-blue-600 transition-colors tracking-tight">{tx.asset}</p>
                                                    <p className="text-[10px] font-bold text-muted-foreground mt-2 uppercase tracking-tighter italic">
                                                        {tx.timestamp?.toDate ? tx.timestamp.toDate().toLocaleString() : 'Processing...'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-8">
                                            <span className="px-3 py-1 rounded-lg bg-accent border border-border text-[9px] font-black text-muted-foreground uppercase tracking-widest">{tx.type}</span>
                                        </td>
                                        <td className="px-10 py-8 text-right font-mono">
                                            <p className="text-lg font-black text-foreground tracking-tighter tabular-nums">{tx.amount}</p>
                                        </td>
                                        <td className="px-10 py-8">
                                            <div className="flex justify-center">
                                                <div className={cn("inline-flex items-center gap-2 px-4 py-1.5 rounded-xl border text-[9px] font-black uppercase tracking-[0.2em] shadow-sm transition-all duration-500", getStatusStyle(tx.status))}>
                                                    <div className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></div>
                                                    {tx.status}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-8 text-right">
                                            <button className="p-3.5 rounded-2xl bg-accent text-muted-foreground/30 hover:text-blue-600 hover:bg-blue-500/10 transition-all border border-transparent hover:border-blue-500/20 active:scale-90">
                                                <MoreVertical className="h-5 w-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HistoryPage;
